import { useMemo, useState } from "react";
import CustomerFormModal from "@/pages/admin/customers/components/CustomerFormModal";
import CustomerDetailModal from "@/pages/admin/customers/components/CustomerDetailModal";
import { useCustomersData } from "@/hooks/useCustomers";
import { CUSTOMER_TYPE_LABELS } from "@/lib/constants";
import { supabase } from "@/lib/supabase";
import { logAudit } from "@/lib/audit";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import type { Customer } from "@/types";

export default function AdminCustomersPage() {
  const toast = useToast();
  const { profile } = useAuth();
  const { customers, loading, error, reload } = useCustomersData();

  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [formOpen, setFormOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [editing, setEditing] = useState<Customer | null>(null);
  const [selected, setSelected] = useState<Customer | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return customers.filter((customer) => {
      if (typeFilter && customer.customer_type !== typeFilter) return false;
      if (statusFilter === "active" && !customer.active) return false;
      if (statusFilter === "inactive" && customer.active) return false;
      if (!term) return true;
      const haystack = `${customer.first_name} ${customer.last_name} ${customer.document ?? ""} ${
        customer.phone ?? ""
      } ${customer.whatsapp ?? ""} ${customer.email ?? ""}`.toLowerCase();
      return haystack.includes(term);
    });
  }, [customers, search, typeFilter, statusFilter]);

  const stats = useMemo(() => {
    const total = customers.length;
    const wholesale = customers.filter((c) => c.customer_type === "mayorista").length;
    const active = customers.filter((c) => c.active).length;
    return { total, wholesale, active, regular: total - wholesale };
  }, [customers]);

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (customer: Customer) => {
    setEditing(customer);
    setFormOpen(true);
  };

  const openDetail = (customer: Customer) => {
    setSelected(customer);
    setDetailOpen(true);
  };

  const handleToggleActive = async (customer: Customer) => {
    setTogglingId(customer.id);
    const { error: updateError } = await supabase
      .from("customers")
      .update({ active: !customer.active, updated_at: new Date().toISOString() })
      .eq("id", customer.id);
    setTogglingId(null);
    if (updateError) {
      toast.error("No se pudo actualizar el cliente.");
      return;
    }
    await logAudit({
      userId: profile?.id ?? null,
      userName: `${profile?.full_name ?? ""} ${profile?.last_name ?? ""}`.trim() || "Usuario",
      action: "editar",
      entity: "customers",
      entityId: customer.id,
      branchId: profile?.branch_id ?? null,
      oldValue: { active: customer.active },
      newValue: { active: !customer.active },
    });
    toast.success(customer.active ? "Cliente desactivado." : "Cliente activado.");
    reload();
  };

  const statCards = [
    { key: "total", label: "Clientes", value: stats.total, icon: "ri-user-3-line", tone: "primary" },
    { key: "wholesale", label: "Mayoristas", value: stats.wholesale, icon: "ri-price-tag-2-line", tone: "accent" },
    { key: "regular", label: "Regulares", value: stats.regular, icon: "ri-user-line", tone: "secondary" },
    { key: "active", label: "Activos", value: stats.active, icon: "ri-checkbox-circle-line", tone: "secondary" },
  ];

  const toneClass: Record<string, string> = {
    primary: "bg-primary-100 text-primary-700",
    accent: "bg-accent-100 text-accent-800",
    secondary: "bg-secondary-200 text-secondary-900",
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground-950">Clientes y Mayoristas</h1>
          <p className="mt-1.5 text-sm text-foreground-500">
            Administra clientes, su historial y vehículos. Los mayoristas reciben precio mayorista
            automáticamente en el POS.
          </p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-add-line text-base"></i>
          Nuevo cliente
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {statCards.map((card) => (
          <div
            key={card.key}
            className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-100 px-4 py-3"
          >
            <span className={`flex h-9 w-9 items-center justify-center rounded-md ${toneClass[card.tone]}`}>
              <i className={`${card.icon} text-base`}></i>
            </span>
            <div>
              <p className="font-heading text-lg font-bold leading-none text-foreground-950">{card.value}</p>
              <p className="mt-1 text-[11px] text-foreground-500">{card.label}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-col gap-3 rounded-lg border border-background-200 bg-background-100 p-4 lg:flex-row lg:items-center">
        <div className="relative flex-1">
          <span className="pointer-events-none absolute left-3 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-foreground-400">
            <i className="ri-search-line text-base"></i>
          </span>
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar por nombre, documento, teléfono o correo..."
            className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-10 pr-3 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-300 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </div>
        <div className="flex flex-wrap gap-3">
          <select
            value={typeFilter}
            onChange={(event) => setTypeFilter(event.target.value)}
            className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          >
            <option value="">Todos los tipos</option>
            <option value="regular">Regulares</option>
            <option value="mayorista">Mayoristas</option>
          </select>
          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          >
            <option value="all">Todos</option>
            <option value="active">Activos</option>
            <option value="inactive">Inactivos</option>
          </select>
        </div>
      </div>

      {error ? (
        <div className="flex flex-col items-start gap-3 rounded-lg border border-primary-200 bg-primary-50 p-4 text-sm text-primary-800 sm:flex-row sm:items-center sm:justify-between">
          <span className="flex items-center gap-2">
            <i className="ri-error-warning-line text-base"></i>
            {error}
          </span>
          <button
            type="button"
            onClick={reload}
            className="whitespace-nowrap rounded-md border border-primary-300 bg-background-50 px-3 py-1.5 text-xs font-semibold text-primary-700"
          >
            Reintentar
          </button>
        </div>
      ) : null}

      {loading ? (
        <div className="flex items-center justify-center gap-3 rounded-lg border border-background-200 bg-background-100 py-14 text-sm text-foreground-500">
          <i className="ri-loader-4-line animate-spin text-lg"></i>
          Cargando clientes...
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-background-300 bg-background-100 px-4 py-14 text-center">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-user-3-line text-2xl"></i>
          </span>
          <p className="text-sm font-semibold text-foreground-800">Sin clientes</p>
          <p className="max-w-sm text-xs text-foreground-500">
            Crea tu primer cliente. Podrás agregarle vehículos y convertirlo en mayorista.
          </p>
        </div>
      ) : (
        <>
          <div className="hidden overflow-hidden rounded-lg border border-background-200 lg:block">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-background-100 text-left text-[11px] font-semibold uppercase tracking-wide text-foreground-500">
                  <th className="px-4 py-3">Cliente</th>
                  <th className="px-4 py-3">Contacto</th>
                  <th className="px-4 py-3">Tipo</th>
                  <th className="px-4 py-3">Estado</th>
                  <th className="px-4 py-3 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-200 bg-background-50">
                {filtered.map((customer) => {
                  const wholesale = customer.customer_type === "mayorista";
                  return (
                    <tr
                      key={customer.id}
                      onClick={() => openDetail(customer)}
                      className="cursor-pointer text-sm text-foreground-800 transition-colors hover:bg-background-100"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <span
                            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
                              wholesale ? "bg-accent-100 text-accent-900" : "bg-secondary-100 text-secondary-900"
                            }`}
                          >
                            {(customer.first_name?.[0] ?? "?").toUpperCase()}
                          </span>
                          <div className="min-w-0">
                            <p className="truncate font-semibold text-foreground-950">
                              {customer.first_name} {customer.last_name}
                            </p>
                            <p className="truncate text-[11px] text-foreground-400">
                              {customer.document ? `Doc: ${customer.document}` : "Sin documento"}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-xs text-foreground-600">{customer.phone || "—"}</p>
                        <p className="text-[11px] text-foreground-400">{customer.email || "Sin correo"}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${
                            wholesale ? "bg-accent-100 text-accent-900" : "bg-secondary-100 text-secondary-900"
                          }`}
                        >
                          {CUSTOMER_TYPE_LABELS[customer.customer_type] ?? customer.customer_type}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${
                            customer.active ? "bg-background-200 text-foreground-700" : "bg-primary-100 text-primary-700"
                          }`}
                        >
                          {customer.active ? "Activo" : "Inactivo"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div
                          className="flex items-center justify-end gap-2"
                          onClick={(event) => event.stopPropagation()}
                        >
                          <button
                            type="button"
                            onClick={() => openDetail(customer)}
                            className="flex h-8 w-8 items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-600 transition-colors hover:bg-background-100"
                          >
                            <i className="ri-eye-line text-sm"></i>
                          </button>
                          <button
                            type="button"
                            onClick={() => openEdit(customer)}
                            className="flex h-8 w-8 items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-600 transition-colors hover:bg-background-100"
                          >
                            <i className="ri-edit-2-line text-sm"></i>
                          </button>
                          <button
                            type="button"
                            disabled={togglingId === customer.id}
                            onClick={() => handleToggleActive(customer)}
                            className={`flex h-8 w-8 items-center justify-center rounded-md border transition-colors disabled:opacity-60 ${
                              customer.active
                                ? "border-primary-200 bg-primary-50 text-primary-700"
                                : "border-accent-300 bg-accent-50 text-accent-900"
                            }`}
                          >
                            <i
                              className={`${
                                customer.active ? "ri-close-circle-line" : "ri-checkbox-circle-line"
                              } text-sm`}
                            ></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:hidden">
            {filtered.map((customer) => {
              const wholesale = customer.customer_type === "mayorista";
              return (
                <div
                  key={customer.id}
                  className="flex flex-col gap-3 rounded-lg border border-background-200 bg-background-100 p-4"
                >
                  <button
                    type="button"
                    onClick={() => openDetail(customer)}
                    className="flex items-start gap-3 text-left"
                  >
                    <span
                      className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-base font-bold ${
                        wholesale ? "bg-accent-100 text-accent-900" : "bg-secondary-100 text-secondary-900"
                      }`}
                    >
                      {(customer.first_name?.[0] ?? "?").toUpperCase()}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-foreground-950">
                        {customer.first_name} {customer.last_name}
                      </p>
                      <p className="truncate text-[11px] text-foreground-400">{customer.phone || "Sin teléfono"}</p>
                      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                        <span
                          className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                            wholesale ? "bg-accent-100 text-accent-900" : "bg-secondary-100 text-secondary-900"
                          }`}
                        >
                          {CUSTOMER_TYPE_LABELS[customer.customer_type] ?? customer.customer_type}
                        </span>
                        {!customer.active ? (
                          <span className="rounded-full bg-primary-100 px-2 py-0.5 text-[10px] font-semibold text-primary-700">
                            Inactivo
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </button>
                  <div className="flex items-center justify-end gap-2 border-t border-background-200 pt-3">
                    <button
                      type="button"
                      onClick={() => openEdit(customer)}
                      className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-600"
                    >
                      <i className="ri-edit-2-line text-base"></i>
                    </button>
                    <button
                      type="button"
                      disabled={togglingId === customer.id}
                      onClick={() => handleToggleActive(customer)}
                      className={`flex h-9 w-9 items-center justify-center rounded-md border disabled:opacity-60 ${
                        customer.active
                          ? "border-primary-200 bg-primary-50 text-primary-700"
                          : "border-accent-300 bg-accent-50 text-accent-900"
                      }`}
                    >
                      <i
                        className={`${customer.active ? "ri-close-circle-line" : "ri-checkbox-circle-line"} text-base`}
                      ></i>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      <CustomerFormModal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSaved={reload}
        customer={editing}
      />

      <CustomerDetailModal
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        customer={selected}
        onChanged={reload}
      />
    </div>
  );
}